/**
 * Capture screen — WO-M1 / WO-M3 (offline-first).
 *
 * Two capture modes:
 *   1. Photo (expo-camera): launches the device camera, takes a photo,
 *      reads the JPEG as base64 (expo-file-system), and enqueues it.
 *   2. Document (expo-document-picker): picks a PDF or image from the device
 *      file system, reads it as base64, and enqueues it.
 *
 * OFFLINE-FIRST FLOW (WO-M3)
 * --------------------------
 * 1. ALWAYS enqueueCapture() first — writes the file to the on-device
 *    SQLite capture_queue with a fresh client UUID. This works with zero
 *    network connectivity; the file is durably stored.
 * 2. If the device is currently online, immediately call syncQueue() to
 *    attempt an upload. The user is not blocked waiting for this.
 * 3. Navigate to review — if the sync succeeded, the BatchResult is already
 *    in the Zustand store; if offline the queue shows a pending badge.
 *
 * The loading state now says "Saving…" (enqueue is instant) and transitions
 * to "Syncing…" if online. If offline it navigates immediately after enqueue.
 *
 * Permissions:
 *   - Camera: requested via useCameraPermissions() before showing the camera.
 *   - Photo library: not needed; we capture live photos or pick documents.
 *   - app.json already declares NSCameraUsageDescription + CAMERA permission.
 *
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 *
 * WO-M0 — design-system refactor. All styling via theme tokens + UI primitives.
 */

import { useState, useRef } from 'react';
import {
  View,
  Pressable,
  StyleSheet,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import { CameraView, useCameraPermissions } from 'expo-camera';
import { enqueueCapture } from '../../src/offline/captureQueue';
import { syncQueue } from '../../src/offline/captureQueue';
import {
  pickDocument,
  capturePhoto,
  readUriAsBase64,
  SUPPORTS_NATIVE_CAMERA,
  type AcquiredFile,
} from '../../src/platform/fileCapture';
import { isOnline } from '../../src/platform/connectivity';
import { useFileDrop } from '../../src/hooks/useFileDrop';
import { ApiRequestError } from '../../src/api/client';
import { Screen, Text, Button, Card } from '../../src/components/ui';
import { theme } from '../../src/theme';
import { CoachmarkOverlay } from '../../src/components/help';
import { FLOW_KEYS, SCREEN_KEYS, useHelpEntry } from '../../src/help';

type CaptureMode = 'idle' | 'camera' | 'saving' | 'syncing' | 'error' | 'success';

export default function CaptureScreen() {
  const router = useRouter();

  const [mode, setMode] = useState<CaptureMode>('idle');
  const captureHelp = useHelpEntry(SCREEN_KEYS.capture);
  const [error, setError] = useState<string | null>(null);
  const [permission, requestPermission] = useCameraPermissions();
  const cameraRef = useRef<CameraView | null>(null);

  // Web (W5) — accept files dropped anywhere on the screen while idle.
  useFileDrop((files) => {
    const file = files[0];
    if (!file) return;
    setError(null);
    setMode('saving');
    void captureAcquired(file);
  }, mode === 'idle' || mode === 'error');

  // ---------------------------------------------------------------------------
  // Photo capture path (expo-camera)
  // ---------------------------------------------------------------------------

  async function handleOpenCamera() {
    // Web has no native CameraView — route through the <input capture> shim instead.
    if (!SUPPORTS_NATIVE_CAMERA) {
      setError(null);
      try {
        const file = await capturePhoto();
        if (!file) {
          setMode('idle');
          return;
        }
        setMode('saving');
        await captureAcquired(file);
      } catch (err) {
        handleCaptureError(err);
      }
      return;
    }
    if (!permission?.granted) {
      const result = await requestPermission();
      if (!result.granted) {
        setError(
          'Camera permission is required to capture receipts. Enable it in Settings.',
        );
        setMode('error');
        return;
      }
    }
    setError(null);
    setMode('camera');
  }

  async function handleTakePhoto() {
    if (!cameraRef.current) return;
    setMode('saving');
    try {
      const photo = await cameraRef.current.takePictureAsync({
        quality: 0.85,
        base64: false,
        exif: false,
      });
      if (!photo?.uri) {
        throw new Error('Camera returned no photo URI.');
      }
      await captureUri(photo.uri, 'receipt.jpg', 'image/jpeg');
    } catch (err) {
      handleCaptureError(err);
    }
  }

  // ---------------------------------------------------------------------------
  // Document picker path (expo-document-picker)
  // ---------------------------------------------------------------------------

  async function handlePickDocument() {
    setError(null);
    try {
      const file = await pickDocument();
      if (!file) {
        setMode('idle');
        return;
      }
      setMode('saving');
      await captureAcquired(file);
    } catch (err) {
      handleCaptureError(err);
    }
  }

  // ---------------------------------------------------------------------------
  // Shared offline-first capture logic (WO-M3)
  // ---------------------------------------------------------------------------

  async function captureUri(uri: string, filename: string, mimeType: string) {
    // Native live-camera path: read the file:// URI as base64, then enqueue + sync.
    const contentBase64 = await readUriAsBase64(uri);
    await captureAcquired({ filename, mimeType, contentBase64 });
  }

  /**
   * Shared offline-first finish step for an already-acquired file (camera, document, or
   * web file-input). 1) enqueue locally (always succeeds); 2) if online, sync immediately
   * so the BatchResult lands in the review store; 3) navigate to review.
   */
  async function captureAcquired(file: AcquiredFile) {
    await enqueueCapture({
      filename: file.filename,
      mimeType: file.mimeType,
      contentBase64: file.contentBase64,
    });

    let online = false;
    try {
      online = await isOnline();
    } catch {
      online = false;
    }

    if (online) {
      setMode('syncing');
      try {
        await syncQueue();
      } catch {
        // Sync failure is non-fatal — the capture is safely queued.
        // The SyncStatusBar on the home/review screens will surface the retry.
      }
    }

    setMode('success');
    // Navigate to review — if synced the batch is in the Zustand store;
    // if offline the queue badge shows pending count.
    router.replace('/(app)/review');
  }

  function handleCaptureError(err: unknown) {
    let message = 'Capture failed. Please try again.';
    if (err instanceof ApiRequestError) {
      message = err.message;
    } else if (err instanceof Error) {
      message = err.message;
    }
    setError(message);
    setMode('error');
  }

  function handleRetry() {
    setError(null);
    setMode('idle');
  }

  // ---------------------------------------------------------------------------
  // Camera view (full-screen) — raw View kept intentionally: the camera overlay
  // must cover the entire screen including safe areas, which Screen prevents.
  // Camera bg uses theme.palette.ink (deepest dark available in tokens).
  // ---------------------------------------------------------------------------

  if (mode === 'camera') {
    return (
      <View style={styles.cameraContainer}>
        <CameraView
          ref={cameraRef}
          style={styles.camera}
          facing="back"
          accessibilityLabel="Camera viewfinder"
        />
        {/* Overlay controls */}
        <View style={styles.cameraControls}>
          <Pressable
            style={styles.cameraCancelButton}
            onPress={() => setMode('idle')}
            accessibilityRole="button"
            accessibilityLabel="Cancel photo capture"
          >
            <Text variant="bodyStrong" color="onPrimary">Cancel</Text>
          </Pressable>

          <Pressable
            style={styles.cameraShutterButton}
            onPress={handleTakePhoto}
            accessibilityRole="button"
            accessibilityLabel="Take photo"
          >
            <View style={styles.cameraShutterInner} />
          </Pressable>

          <View style={styles.cameraShutterPlaceholder} />
        </View>
      </View>
    );
  }

  // ---------------------------------------------------------------------------
  // Saving / syncing states
  // ---------------------------------------------------------------------------

  if (mode === 'saving' || mode === 'syncing') {
    const label = mode === 'saving' ? 'Saving capture…' : 'Syncing to server…';
    const sublabel =
      mode === 'saving'
        ? 'Storing your document locally.'
        : 'Uploading and extracting your document.';
    return (
      <Screen>
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={theme.color.action.primary} />
          <Text variant="subhead">{label}</Text>
          <Text variant="body" color="secondary" style={styles.centered}>{sublabel}</Text>
        </View>
      </Screen>
    );
  }

  // ---------------------------------------------------------------------------
  // Default idle + error view
  // ---------------------------------------------------------------------------

  return (
    <Screen scroll padded>
      <CoachmarkOverlay flowKey={FLOW_KEYS.captureReceipt} />
      <Text variant="title">Capture Receipt</Text>
      <Text variant="body" color="secondary">
        {captureHelp?.body ??
          'Photograph a receipt or pick a document (PDF or image) to extract facts for review. Captures are saved locally and synced when online.'}
      </Text>

      {/* Error */}
      {mode === 'error' && error ? (
        <Card style={styles.errorCard}>
          <Text variant="body" color="danger" accessibilityRole="alert">{error}</Text>
          <Button
            title="Dismiss"
            variant="danger"
            size="sm"
            fullWidth={false}
            onPress={handleRetry}
            accessibilityLabel="Dismiss error"
          />
        </Card>
      ) : null}

      {/* Actions */}
      <View style={styles.actions}>
        {/* Camera — iOS and Android */}
        <Pressable
          style={({ pressed }) => [
            styles.actionCard,
            pressed && styles.actionCardPressed,
          ]}
          onPress={handleOpenCamera}
          accessibilityRole="button"
          accessibilityLabel="Take a photo with camera"
        >
          <Text variant="title" accessibilityElementsHidden>{'📷'}</Text>
          <Text variant="subhead">Take Photo</Text>
          <Text variant="small" color="secondary">
            Use the device camera to photograph a receipt
          </Text>
        </Pressable>

        {/* Document picker */}
        <Pressable
          style={({ pressed }) => [
            styles.actionCard,
            pressed && styles.actionCardPressed,
          ]}
          onPress={handlePickDocument}
          accessibilityRole="button"
          accessibilityLabel="Pick a document from files"
        >
          <Text variant="title" accessibilityElementsHidden>{'📄'}</Text>
          <Text variant="subhead">Pick Document</Text>
          <Text variant="small" color="secondary">
            Select a PDF or image from your device
          </Text>
        </Pressable>
      </View>

      {/* Offline note */}
      <Card style={styles.noteCard}>
        <Text variant="small" color="secondary">
          Captures are always saved locally first. They sync automatically
          when a connection is available — you can capture offline.
        </Text>
      </Card>

      {/* Navigation back */}
      <Button
        title="Back to Home"
        variant="secondary"
        onPress={() => router.back()}
        accessibilityLabel="Go back to home"
      />
    </Screen>
  );
}

const styles = StyleSheet.create({
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
  },
  errorCard: {
    backgroundColor: theme.status.overdue.bg,
    borderColor: theme.status.overdue.bg,
  },
  actions: {
    gap: theme.spacing.md,
  },
  actionCard: {
    backgroundColor: theme.color.bg.surface,
    borderRadius: theme.radius.lg,
    padding: theme.spacing.xl,
    gap: theme.spacing.xs,
    ...theme.shadow.card,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.default,
    alignItems: 'flex-start',
  },
  actionCardPressed: {
    backgroundColor: theme.color.bg.subtle,
  },
  noteCard: {
    backgroundColor: theme.status.paid.bg,
    borderColor: theme.status.paid.bg,
  },
  // Camera overlay — full-screen hardware UI. Uses deepest dark palette token.
  cameraContainer: {
    flex: 1,
    backgroundColor: theme.palette.ink,
  },
  camera: {
    flex: 1,
  },
  cameraControls: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingBottom: Platform.OS === 'ios' ? theme.spacing['4xl'] + theme.spacing.sm : theme.spacing['3xl'],
    paddingHorizontal: theme.spacing['3xl'],
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cameraCancelButton: {
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.xs,
  },
  cameraShutterButton: {
    width: 72,
    height: 72,
    borderRadius: theme.radius.xl,
    backgroundColor: theme.palette.white,
    opacity: 0.9,
    borderWidth: theme.spacing.xs,
    borderColor: theme.palette.white,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cameraShutterInner: {
    width: 52,
    height: 52,
    borderRadius: theme.radius.xl,
    backgroundColor: theme.palette.white,
  },
  cameraShutterPlaceholder: {
    width: theme.layout.minTapTarget,
  },
});
